import React, { FC, useState, useMemo, useEffect } from 'react';
import { JobPostResponse, JobType, PostStatus, PostType } from '../../types/api';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

interface AllPostsTableProps {
  jobPosts: JobPostResponse[];
  isLoading: boolean;
  error: string | null;
}

const AllPostsTable: FC<AllPostsTableProps> = ({ jobPosts, isLoading, error }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    status: searchParams.get('status') as PostStatus || '',
    type: searchParams.get('type') as JobType | null,
    minSalary: searchParams.get('minSalary') || '',
    maxSalary: searchParams.get('maxSalary') || '',
  });
  const { pathname } = useLocation();
  const userRole = pathname.split('/')[1];
  
  const handleFilterChange = (name: string, value: string | null) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) {
      newSearchParams.set(name, value);
    } else {
      newSearchParams.delete(name);
    }
    setSearchParams(newSearchParams);
  };

  useEffect(() => {
    setFilters({
      status: searchParams.get('status') as PostStatus || '',
      type: searchParams.get('type') as JobType | null,
      minSalary: searchParams.get('minSalary') || '',
      maxSalary: searchParams.get('maxSalary') || '',
    });
  }, [searchParams]);

  const filteredJobPosts = useMemo(() => {
    return jobPosts.filter(post => {
      const { status, type, minSalary, maxSalary } = filters;
      return (
        (status ? post.postStatus === status : true) &&
        (type ? post.jobType === type : true) &&
        (minSalary ? (post.minSalary || 0) >= parseInt(minSalary) : true) &&
        (maxSalary ? (post.maxSalary || 0) <= parseInt(maxSalary) : true)
      );
    });
  }, [jobPosts, filters]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500">Error: {error}</div>;
  }

  return (
    <div>
      <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Select
          label="Status"
          placeholder="Filter by Status"
          options={[
            { value: "All", label: "All Statuses" },
            { value: "Active", label: "Active" },
            { value: "Complete", label: "Complete" },
            { value: "Draft", label: "Draft" },
            { value: "UnderReview", label: "Under Review" },
          ]}
          value={filters.status || "All"}
          onChange={value => handleFilterChange('status', value === "All" ? "" : value)}
          native={true}
        />
        <Select
          label="Type"
          placeholder="Filter by Type"
          options={[
            { value: 'All', label: "All Types" },
            { value: "FullTime", label: "Full-Time" },
            { value: "PartTime", label: "Part-Time" },
            { value: "Contract", label: "Contract" },
            { value: "Internship", label: "Internship" },
            { value: "Temporary", label: "Temporary" },
          ] as {value : JobType | 'All', label : String}[] as any }
          value={filters.type || 'All'}
          onChange={value => handleFilterChange('type', value === 'All' ? null : value)}
          native={true}
        />

        <Input
          label= 'Min Salary'
          type="number"
          placeholder="10000"
          value={filters.minSalary}
          className='space-y-0'
          onChange={e => handleFilterChange('minSalary', e.target.value)}
        />
        <Input
          label= 'Max Salary'
          type="number"
          placeholder="100000"
          value={filters.maxSalary}
          onChange={e => handleFilterChange('maxSalary', e.target.value)}
        />
      </div>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No. Openings</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work Mode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salary</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredJobPosts.map(post => (
                <tr key={post.postId} className="hover:bg-primary-teaGreen/30">
                  <td className="px-6 py-4 whitespace-nowrap">{post.noOfOpenings}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{post.jobTitle}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{post.companyName}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{post.postStatus}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{post.jobType}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{post.workMode}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{`${post.minSalary || 'N/A'} - ${post.maxSalary || 'N/A'} ${post.salaryCurrency || ''}`}</td>
                  <td >
                    <Link to={`/${userRole}/post/${post.postId}`}>
                      <Button variant='secondary' size="sm">View</Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AllPostsTable;